import { useCallback } from 'react';
import { useNotifications } from 'modules/notification';
import { useLazyQuery, useMutation } from '@apollo/client';

import { adminExternalDashboardsApi } from './index';
import { DashboardMutationResponse, DashboardQueryResponse } from '../types';

export function useExternalDashboards() {
  const { showNotification } = useNotifications();

  const [onCreate, { loading: isCreating }] = useMutation<DashboardMutationResponse>(
    adminExternalDashboardsApi.createDashboard,
    {
      refetchQueries: [adminExternalDashboardsApi.getAdminDashboardList],
      awaitRefetchQueries: true,
    }
  );

  const handleCreate = useCallback(
    async (dto: { name: string; icon: string; externalId: string; description: string | null; type: string }) => {
      try {
        const { data } = await onCreate({
          variables: {
            input: dto,
          },
        });

        if (data?.externalDashboards?.result?.error && !data?.externalDashboards?.result?.success) {
          showNotification(data?.externalDashboards?.result?.error, { type: 'error' });
          return;
        }

        showNotification('Dashboard successfully created');

        return data?.externalDashboards?.result?.entity;
      } catch (error) {
        showNotification('Something went wrong', { type: 'error' });
      }
    },
    [onCreate, showNotification]
  );

  const [onUpdate, { loading: isUpdating }] = useMutation<DashboardMutationResponse>(
    adminExternalDashboardsApi.updateDashboard,
    {
      refetchQueries: [adminExternalDashboardsApi.getAdminDashboardList],
      awaitRefetchQueries: true,
    }
  );

  const handleUpdate = useCallback(
    async (dto: {
      id: string;
      name: string;
      icon: string;
      type: string;
      externalId: string;
      description: string | null;
    }) => {
      try {
        const { data } = await onUpdate({
          variables: {
            input: dto,
          },
        });

        if (data?.externalDashboards?.result?.error && !data?.externalDashboards?.result?.success) {
          showNotification(data?.externalDashboards?.result?.error, { type: 'error' });
          return;
        }

        showNotification('Dashboard successfully created');

        return data?.externalDashboards?.result?.entity;
      } catch (error) {
        showNotification('Something went wrong', { type: 'error' });
      }
    },
    [onUpdate, showNotification]
  );

  const [onDelete, { loading: isDeleting }] = useMutation(adminExternalDashboardsApi.deleteDashboard, {
    refetchQueries: [adminExternalDashboardsApi.getAdminDashboardList],
    awaitRefetchQueries: true,
  });

  const handleDelete = useCallback(
    async ({ id }: { id: string }) => {
      try {
        const { data } = await onDelete({
          variables: {
            input: {
              id,
            },
          },
        });

        if (data?.externalDashboards?.result?.error && !data?.externalDashboards?.result?.success) {
          showNotification(data?.externalDashboards?.result?.error, { type: 'error' });
          return;
        }

        showNotification('Dashboard successfully deleted');

        return data?.externalDashboards?.result?.entity;
      } catch (error) {
        showNotification('Something went wrong', { type: 'error' });
      }
    },
    [onDelete, showNotification]
  );

  const [onUpdateDashboardAccessByAccount, { loading: isUpdatingAccessByAccount }] =
    useMutation<DashboardMutationResponse>(adminExternalDashboardsApi.updateDashboardAccessByAccount, {
      refetchQueries: [adminExternalDashboardsApi.getAdminDashboardList],
      awaitRefetchQueries: true,
    });

  const handleUpdateAccessByAccount = useCallback(
    async (listOfAccess: { accounts: string[]; dashboardId: string; accountTypes: string[] }[]) => {
      try {
        const { data } = await onUpdateDashboardAccessByAccount({
          variables: {
            input: {
              listOfAccess,
            },
          },
        });

        if (data?.externalDashboards?.result?.error && !data?.externalDashboards?.result?.success) {
          showNotification(data?.externalDashboards?.result?.error, { type: 'error' });
          return;
        }

        showNotification('Dashboard access updated');

        return data?.externalDashboards?.result?.entity;
      } catch (error) {
        showNotification('Something went wrong', { type: 'error' });
      }
    },
    [onUpdateDashboardAccessByAccount, showNotification]
  );

  const [onFetchDashboard, { loading: isLoadingDashboard }] = useLazyQuery<DashboardQueryResponse>(
    adminExternalDashboardsApi.getDashboard
  );

  const handleFetchDashboard = useCallback(
    async ({ id }: { id: string }) => {
      try {
        const { data } = await onFetchDashboard({
          variables: {
            input: {
              id,
            },
          },
        });

        if (data?.externalDashboards?.result?.error && !data?.externalDashboards?.result?.success) {
          showNotification(data?.externalDashboards?.result?.error, { type: 'error' });
          return;
        }

        return data?.externalDashboards?.result?.entity;
      } catch (error) {
        showNotification('Something went wrong', { type: 'error' });
      }
    },
    [onFetchDashboard, showNotification]
  );

  return {
    isCreating,
    isDeleting,
    isUpdating,
    isLoadingDashboard,
    isUpdatingAccessByAccount,
    handleCreate,
    handleDelete,
    handleUpdate,
    handleFetchDashboard,
    handleUpdateAccessByAccount,
  };
}
